import React, { JSX, useState } from "react";
import { Button, TextField, Typography } from "@platform/design-system";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";

const PDF_EXTENSION = ".pdf";
const MAX_FILE_NAME_LENGTH = 100;

// The extension is shown as an adornment and appended on save, so a name typed
// or pasted with it would otherwise be stored as "name.pdf.pdf".
const stripPdfExtension = (value: string): string =>
  value.replace(/\.pdf$/i, "");

export type RenameDocumentModalProps = {
  isOpen: boolean;
  currentFileName: string;
  onClose: VoidFunction;
  onConfirm: (newFileName: string) => Promise<void>;
};

export const RenameDocumentModal = ({
  isOpen,
  currentFileName,
  onClose,
  onConfirm,
}: RenameDocumentModalProps): JSX.Element => {
  const [newBaseName, setNewBaseName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const currentBaseName = stripPdfExtension(currentFileName);
  const trimmedNewBaseName = stripPdfExtension(newBaseName.trim());
  const isBlank = trimmedNewBaseName.length === 0;
  const isTooLong = trimmedNewBaseName.length > MAX_FILE_NAME_LENGTH;
  const matchesCurrentName = !isBlank && trimmedNewBaseName === currentBaseName;

  const getError = (): string => {
    if (isTooLong) {
      return `File name cannot exceed ${MAX_FILE_NAME_LENGTH} characters.`;
    }

    if (matchesCurrentName) {
      return "New name must be different from the current name.";
    }

    return "";
  };

  const error = getError();
  const canSave = !isBlank && !matchesCurrentName && !isTooLong;

  const handleClose = (): void => {
    if (isSaving) {
      return;
    }

    setNewBaseName("");
    onClose();
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setNewBaseName(event.target.value);
  };

  const handleConfirm = async (): Promise<void> => {
    if (!canSave || isSaving) {
      return;
    }

    setIsSaving(true);

    try {
      await onConfirm(`${trimmedNewBaseName}${PDF_EXTENSION}`);
      setNewBaseName("");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      data-testid="rename-document-modal"
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Rename Document</DialogTitle>
      <DialogContent>
        <Stack sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Current Document Name:
          </Typography>
          <Typography
            variant="caption"
            sx={{
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}
          >
            {currentFileName}
          </Typography>
        </Stack>

        <Typography variant="body-medium" sx={{ mb: 1, display: "block" }}>
          Update document name
        </Typography>

        <TextField
          fullWidth
          placeholder="Enter new name..."
          value={newBaseName}
          onChange={handleChange}
          disabled={isSaving}
          error={!!error}
          helperText={error}
          endAdornment={PDF_EXTENSION}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="tertiary" disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleConfirm()}
          variant="primary"
          loading={isSaving}
          disabled={!canSave}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
